import { FormEvent, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, Check, ExternalLink, LoaderCircle, PanelLeft, PanelRight, Pencil, Pin, Plus, RefreshCw, Search, Tag, Trash2, X } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ApiException, SelectionRange } from '@/generated/api-client';

import { appShellScrollFrame } from '@/services/scrollFrame';
import { CardFeedPanel } from '@/components/feed/CardFeedPanel';
import { usePhraseChips } from '@/components/PhraseChips';
import { CardChatPanel } from '@/components/chat/CardChatPanel';
import { CardVideosPanel } from '@/components/videos/CardVideosPanel';
import { HighlightedSelections } from '@/components/HighlightedSelections';
import { HighlightedSlots } from '@/components/HighlightedSlots';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SpeakButton } from '@/components/SpeakButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  apiClient,
  pronunciationUrl,
  type MeaningCreationResponse,
  type PhraseChip,
  type PhraseTokenDto,
  type MeaningResult,
  type WeeklyStatistics,
} from '@/services';
import { cn } from '@/services/utils';

import { CardCollocations } from './CardCollocations';
import { CreateCardFab } from './CreateCardFab';
import { ContextualCardDialog } from './ContextualCardDialog';
import { ManualCardDialog } from './ManualCardDialog';
import { getTagColor } from './tagColors';
import { AppTopHeader } from '@/components/AppTopHeader';
import { MEANING_GRID_COLS_CLASS, MEANING_ROW_MAX_WIDTH_CLASS, MEANING_DETAIL_LEFT_REFERENCE_WIDTH } from './layoutGrid';
import { ThisWeekWidget } from './ThisWeekWidget';
import { TagsSidebarList, type CreateSidebarTagResult } from './TagsSidebarList';
import { ManageTagsDialog } from './ManageTagsDialog';
import { LiveMentionsWidget } from './LiveMentionsWidget';
import { MeaningFeedCard } from './MeaningFeedCard';

// Shared between the list's aside and the detail shell's aside so framer-motion animates the
// position change (list frame -> wider detail frame) as a slide instead of a hard jump.
const MEANING_LEFT_RAIL_LAYOUT_ID = 'meaning-left-rail';

export type MeaningCard = {
  id?: string;
  phrase: string;
  isSlotted: boolean;
  canonical: string;
  sound?: string;
  selections: SelectionRange[];
  phraseChips: PhraseChip[];
  phraseTokens: PhraseTokenDto[];
  translation: string;
  context: string;
  definition: string;
  examples: MeaningExampleCard[];
  alternatives: MeaningAlternativeCard[];
  // Alternatives come from a separate request after the card is created; until it answers the card
  // shows placeholder chips instead of an empty block.
  alternativesPending?: boolean;
  tags: MeaningTagCard[];
};

type DetailNavigationState = {
  returnTo?: string;
  useHistoryBack?: boolean;
};

type MeaningAlternativeCard = {
  alternative: string;
  score?: number;
};

type MeaningTagCard = {
  id: string;
  name: string;
  cardCount?: number;
};

type MeaningExampleCard = {
  exampleIndex: number;
  text: string;
  translation: string;
  pinned: boolean;
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const getMeaningLayoutId = (card: MeaningCard, fallback?: string | number) =>
  `meaning-card-${card.id ?? `${slugify(card.phrase)}-${fallback ?? 'single'}`}`;

// Keeps the document tall enough to hold the current scroll position while the list is swapped for
// the detail view, and stops scroll anchoring from snapping the page to the top when the list goes.
const reserveDocumentScrollRoom = (offset: number) => {
  document.documentElement.style.minHeight = `${offset + window.innerHeight}px`;
  document.documentElement.style.overflowAnchor = 'none';
};

const releaseDocumentScrollRoom = () => {
  document.documentElement.style.minHeight = '';
  document.documentElement.style.overflowAnchor = '';
};

const getAppShell = () => document.querySelector<HTMLElement>('[data-app-shell]');

// Written straight to the DOM rather than through a render: the shell also picks the offset up from
// `appShellScrollFrame` when it re-renders on navigation, and these two must never disagree.
const setAppShellOffset = (offset: number) => {
  appShellScrollFrame.offset = offset;
  const shell = getAppShell();
  if (!shell) return false;

  shell.style.position = offset ? 'relative' : '';
  shell.style.top = offset ? `${offset}px` : '';
  return true;
};

const titleFromSlug = (slug?: string) =>
  slug
    ? slug
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
  : '';

const buildYouglishUrl = (phrase: string) =>
  `https://youglish.com/pronounce/${encodeURIComponent(phrase.trim().replace(/\s+/g, '_'))}/english`;

const getNestedMeaning = (meaning: MeaningResult) => meaning.meaning;

const firstNonBlank = (...values: Array<string | undefined>) =>
  values.find((value) => value?.trim())?.trim();

const getSourceContext = (textInput?: string, phrase?: string) => {
  const input = textInput?.trim() ?? '';
  const target = phrase?.trim() ?? '';

  if (!input || input.toLocaleLowerCase() === target.toLocaleLowerCase()) return '';

  return input;
};

// Matches MEANING_DESKTOP_LAYOUT_QUERY below (Tailwind's `md`) so the side panel's default
// open/closed state agrees with whether the persistent header/This-week shell is showing at all —
// otherwise the shell can render (desktop chrome, card+panel area available) while this checked a
// stricter threshold and defaulted the panel closed, leaving the card alone to fill that whole
// area with nothing to balance the left column against.
const isDesktopFeedViewport = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

// Matches Tailwind's `md` breakpoint, the same one every `md:hidden` / `hidden md:flex` pair in
// this file switches on. The card-detail view can't use that CSS-only dual-tree pattern (unlike
// the list) without mounting ExpandedMeaningCard twice and doubling its live-mentions/videos
// fetches, so it picks one wrapper reactively via this hook instead.
const MEANING_DESKTOP_LAYOUT_QUERY = '(min-width: 768px)';

function useIsDesktopLayout() {
  const [isDesktopLayout, setIsDesktopLayout] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(MEANING_DESKTOP_LAYOUT_QUERY).matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(MEANING_DESKTOP_LAYOUT_QUERY);
    const updateMatch = () => setIsDesktopLayout(mediaQuery.matches);

    updateMatch();
    mediaQuery.addEventListener('change', updateMatch);
    return () => mediaQuery.removeEventListener('change', updateMatch);
  }, []);

  return isDesktopLayout;
}

type CardSidePanel = 'feed' | 'chat' | 'videos' | null;

const getDefaultCardSidePanel = (): CardSidePanel => isDesktopFeedViewport() ? 'feed' : null;

const getMeaningTitleSize = (text: string) => {
  const length = text.trim().length;

  if (length > 180) return 'text-2xl leading-snug sm:text-3xl';
  if (length > 110) return 'text-3xl leading-snug sm:text-4xl';
  if (length > 60) return 'text-4xl leading-tight sm:text-5xl';
  return 'text-5xl leading-tight sm:text-6xl';
};

const normalizeAlternatives = (
  alternatives: { alternative?: string; score?: number | null }[]
): MeaningAlternativeCard[] =>
  alternatives
    .map((alternative) => ({
      alternative: alternative.alternative ?? '',
      score: alternative.score ?? undefined,
    }))
    .filter((alternative) => alternative.alternative.trim());

export const normalizeMeaning = (meaning: MeaningResult): MeaningCard => {
  const nested = getNestedMeaning(meaning);
  const alternatives = nested?.alternatives ?? [];
  const textInput = nested?.textInput ?? '';
  const selections = nested?.selections ?? [];
  const phrase = firstNonBlank(
    (nested as unknown as { displayedText?: string })?.displayedText,
    nested?.dictionaryForm,
    textInput
  ) ?? 'Untitled';
  const sourceContext = getSourceContext(textInput, phrase);

  return {
    id: meaning.id,
    phrase,
    isSlotted: nested?.isSlotted ?? false,
    canonical: firstNonBlank(nested?.canonical, phrase) ?? phrase,
    sound: nested?.sound ?? pronunciationUrl(meaning.id),
    selections,
    phraseChips: nested?.phraseChips ?? [],
    phraseTokens: nested?.phraseTokens ?? [],
    translation: nested?.translation ?? '',
    context: sourceContext,
    definition: nested?.definition ?? '',
    examples: (nested?.examples ?? []).map((example, exampleIndex) => ({
      exampleIndex,
      text: example.text ?? '',
      translation: example.translation ?? '',
      pinned: example.pinned ?? false,
    })),
    alternatives: normalizeAlternatives(alternatives),
    alternativesPending: nested?.alternativesPending ?? false,
    tags: (meaning.tags ?? [])
      .map((tag) => ({
        id: tag.id ?? '',
        name: tag.name ?? '',
      }))
      .filter((tag) => tag.id && tag.name),
  };
};

const normalizeTags = (tags: Awaited<ReturnType<typeof apiClient.replaceMeaningTags>>): MeaningTagCard[] =>
  tags
    .map((tag) => ({ id: tag.id ?? '', name: tag.name ?? '' }))
    .filter((tag) => tag.id && tag.name);

const normalizeTagStatistics = (statistics: Awaited<ReturnType<typeof apiClient.getTagStatistics>>) => ({
  totalCardCount: statistics.totalCardCount ?? 0,
  totalTagCount: statistics.totalTagCount ?? 0,
  tags: (statistics.tags ?? [])
    .map((tag) => ({
      id: tag.id ?? '',
      name: tag.name ?? '',
      cardCount: tag.cardCount ?? 0,
    }))
    .filter((tag) => tag.id && tag.name),
});

const getCreatedMeaning = (response: MeaningCreationResponse) =>
  response.meaning ?? null;

export const getMeaningPageItems = (page: Awaited<ReturnType<typeof apiClient.getPagedMeanings>>) =>
  page.items ?? [];

export const getMeaningPageNextCursor = (page: Awaited<ReturnType<typeof apiClient.getPagedMeanings>>) =>
  page.nextCursor ?? null;

export const getMeaningPageHasMore = (page: Awaited<ReturnType<typeof apiClient.getPagedMeanings>>) =>
  page.hasMore ?? Boolean(getMeaningPageNextCursor(page));

export const getMeaningCardKey = (card: MeaningCard) =>
  card.id ? `id:${card.id}` : `phrase:${slugify(card.phrase)}`;

export const getMeaningCardPath = (card: MeaningCard) =>
  card.id ? `/meaning/card/${card.id}` : slugify(card.phrase) ? `/meaning/${slugify(card.phrase)}` : '/meaning';

const meaningScrollPositionKey = 'lexora:meaning:scroll-position';

export const appendUniqueMeanings = (current: MeaningCard[], next: MeaningCard[]) => {
  const seen = new Set(current.map(getMeaningCardKey));
  const uniqueNext = next.filter((card) => {
    const key = getMeaningCardKey(card);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return [...current, ...uniqueNext];
};

function ScramblingText({ text }: { text: string }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <span className="opacity-30">{text}</span>;
  }

  return (
    <motion.span
      aria-label={text}
      className="inline will-change-[filter,opacity]"
      animate={{
        opacity: [0.5, 0.16, 0.5],
        filter: ['blur(1.25px)', 'blur(4px)', 'blur(1.25px)'],
      }}
      transition={{
        duration: 3.6,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {text}
    </motion.span>
  );
}

const regenerationPulse = {
  opacity: [0.5, 0.16, 0.5],
  filter: ['blur(1.25px)', 'blur(4px)', 'blur(1.25px)'],
};

const regenerationPulseTransition = {
  duration: 3.6,
  repeat: Infinity,
  ease: 'easeInOut' as const,
};

function MeaningDetails({
  card,
  compact = false,
  pinning,
  regeneratingCard = false,
  regeneratingExamples = false,
  onPin,
  onRegenerate,
}: {
  card: MeaningCard;
  compact?: boolean;
  pinning?: string;
  regeneratingCard?: boolean;
  regeneratingExamples?: boolean;
  onPin?: (exampleIndex: number, pinned: boolean) => void;
  onRegenerate?: () => void;
}) {
  const textClassName = compact
    ? 'text-[13px] leading-[18px] text-black'
    : 'text-[15px] leading-6 text-black';
  const isRegeneratingExample = (example: MeaningExampleCard) =>
    regeneratingExamples && !example.pinned;

  const renderExamples = () =>
    card.examples.length > 0 && (
      <div className={cn(compact ? 'space-y-2' : 'space-y-5')}>
        <AnimatePresence initial={false} mode="popLayout">
          {card.examples.map((example) => (
            <motion.div
              key={`${example.pinned ? 'pinned' : 'example'}-${example.text}-${example.translation}`}
              initial={{ opacity: 0, y: 6, filter: 'blur(5px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -4, filter: 'blur(4px)' }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={cn(
                'group/example',
                compact ? 'text-[13px] leading-[18px]' : 'text-[15px] leading-6'
              )}
            >
              <p className="text-black">
                {isRegeneratingExample(example)
                  ? <ScramblingText text={example.text} />
                  : example.text}
                {!compact && onPin && (
                  <motion.button
                    type="button"
                    aria-label={example.pinned ? 'Unpin example' : 'Pin example'}
                    disabled={
                      regeneratingCard ||
                      pinning === `${example.exampleIndex}` ||
                      regeneratingExamples
                    }
                    onClick={() => onPin(example.exampleIndex, !example.pinned)}
                    className={cn(
                      'ml-1 inline-flex translate-y-0.5 rounded p-0.5 transition hover:bg-blue-50 hover:text-blue-600 focus-visible:opacity-100',
                      example.pinned
                        ? 'text-blue-600 opacity-100'
                        : isRegeneratingExample(example)
                          ? 'text-slate-400 opacity-100'
                          : 'text-slate-400 opacity-0 group-hover/example:opacity-100 group-focus-within/example:opacity-100'
                    )}
                    animate={isRegeneratingExample(example) ? regenerationPulse : undefined}
                    transition={isRegeneratingExample(example) ? regenerationPulseTransition : undefined}
                  >
                    <Pin className={cn('size-3.5', example.pinned && 'fill-current')} />
                  </motion.button>
                )}
              </p>
              <p className={cn(compact ? 'text-slate-500' : 'text-slate-500')}>
                {isRegeneratingExample(example) ? (
                  <motion.span
                    aria-hidden="true"
                    className="inline-block will-change-[filter,opacity]"
                    animate={regenerationPulse}
                    transition={regenerationPulseTransition}
                  >
                    {'\u2192'}
                  </motion.span>
                ) : (
                  <span aria-hidden="true">{'\u2192'}</span>
                )}{' '}
                {isRegeneratingExample(example)
                  ? <ScramblingText text={example.translation} />
                  : example.translation}
              </p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    );

  const sectionHeader = (title: string) => compact ? null : (
    <div className="border-t border-slate-200 pt-5">
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">{title}</p>
    </div>
  );

  if (!card.definition.trim() && card.translation.trim()) {
    return (
      <p className={cn('whitespace-pre-line', textClassName, compact ? 'text-slate-500' : 'text-black')}>
        {card.translation}
      </p>
    );
  }

  return (
    <div className={cn(compact ? 'space-y-2' : 'space-y-5')}>
      {compact ? (
        <>
          {card.context && (
            <div className="border-l-2 border-amber-400 pl-3 text-[13px] italic leading-[20px] text-slate-500">
              <HighlightedSelections text={card.context} selections={card.selections} />
            </div>
          )}
          <div className="h-px bg-slate-200" />
          <div className="space-y-1">
            {card.translation && (
              <p className="whitespace-pre-line text-[13px] leading-[18px] text-slate-500">{card.translation}</p>
            )}
            <p className={cn('whitespace-pre-line', textClassName)}>{card.definition}</p>
          </div>
          {card.examples.length > 0 && (
            <>
              <div className="h-px bg-slate-200" />
              <div className="space-y-2">
                {card.examples.slice(0, 2).map((example, exampleIndex) => (
                  <div
                    key={`${example.text}-${example.translation}`}
                    className={cn(
                      'text-[13px] leading-[18px]',
                      exampleIndex === 0 ? 'text-black' : 'text-slate-400'
                    )}
                  >
                    <p>{example.text}</p>
                    {exampleIndex === 0 && example.translation && (
                      <p className="text-slate-500">{'\u2192'} {example.translation}</p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {sectionHeader('Definition')}
          <div className="space-y-1">
            {card.translation && (
              <p className="whitespace-pre-line text-[15px] leading-6 text-slate-500">{card.translation}</p>
            )}
            <p className={cn('whitespace-pre-line', textClassName)}>{card.definition}</p>
          </div>
          {card.examples.length > 0 && (
            <>
              {sectionHeader('Examples')}
              {renderExamples()}
              {onRegenerate && (
                <div className="flex justify-center pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={
                      regeneratingCard ||
                      regeneratingExamples ||
                      Boolean(pinning)
                    }
                    onClick={() => onRegenerate()}
                    className="gap-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                  >
                    {regeneratingExamples ? (
                      <LoaderCircle className="size-4 animate-spin" />
                    ) : (
                      <RefreshCw className="size-4" />
                    )}
                    Show other examples
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// Ghost widths vary so the placeholder row has the rhythm of real chips rather than three equal blocks.
const ALTERNATIVE_GHOST_WIDTHS = [152, 112, 168];
const ALTERNATIVE_EASE = [0.22, 1, 0.36, 1] as const;

function MeaningAlternatives({
  alternatives,
  pending,
  failed,
  onRetry,
}: {
  alternatives: MeaningAlternativeCard[];
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const [contentHeight, setContentHeight] = useState<number>();
  const observerRef = useRef<ResizeObserver | null>(null);

  // The ghost row and the chip row can differ in height (chips may wrap), so the box follows the
  // measured content height instead of snapping when one row replaces the other. A callback ref,
  // because the section (and this node) mounts and unmounts with the block's visibility.
  const contentRef = useCallback((element: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!element) return;

    const observer = new ResizeObserver(() => setContentHeight(element.offsetHeight));
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  const visible = pending || failed || alternatives.length > 0;
  const showError = failed && !pending && alternatives.length === 0;
  const phase = pending ? 'ghosts' : showError ? 'error' : 'chips';
  const fade = reduceMotion ? { duration: 0 } : { duration: 0.2, ease: 'easeOut' as const };
  const settle = reduceMotion ? { duration: 0 } : { duration: 0.3, ease: ALTERNATIVE_EASE };

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.section
          key="alternatives"
          className="mt-8 overflow-hidden border-t border-[#eadfcd] pt-5"
          aria-labelledby="meaning-alternatives"
          aria-busy={pending}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0, marginTop: 0, paddingTop: 0, borderTopWidth: 0 }}
          transition={settle}
        >
          <h2
            id="meaning-alternatives"
            className="text-sm font-semibold uppercase tracking-[0.18em] text-[#496653]"
          >
            Alternatives
          </h2>
          <motion.div
            className="mt-4"
            initial={false}
            animate={{ height: contentHeight ?? 'auto' }}
            transition={settle}
          >
            <div ref={contentRef}>
              {/* Ghosts fade out together before the chips come in: swapping them one by one reflows
                  the row on every swap, because a real chip never has its ghost's width. */}
              <AnimatePresence mode="wait" initial={false}>
                {phase === 'ghosts' && (
                  <motion.div
                    key="ghosts"
                    className="flex min-h-8 flex-wrap gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={fade}
                  >
                    <span className="sr-only">Loading alternatives</span>
                    {ALTERNATIVE_GHOST_WIDTHS.map((width, index) => (
                      <span
                        key={width}
                        aria-hidden="true"
                        className={cn(
                          'block h-8 shrink-0 rounded-full bg-[linear-gradient(90deg,#f1efe9_25%,#f8f6f1_50%,#f1efe9_75%)] bg-[length:250%_100%]',
                          !reduceMotion && 'animate-shimmer'
                        )}
                        style={{ width, animationDelay: `${index * 0.18}s` }}
                      />
                    ))}
                  </motion.div>
                )}
                {phase === 'error' && (
                  <motion.div
                    key="error"
                    className="flex min-h-8 items-center gap-2.5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={fade}
                  >
                    <span className="text-sm text-[#6f6758]">Couldn’t load alternatives</span>
                    <button
                      type="button"
                      onClick={onRetry}
                      className="inline-flex h-7 items-center rounded-full border border-[#d9cbb7] px-3 text-xs font-bold text-[#3d3a33] transition-colors duration-150 hover:bg-[#fffaf1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#496653]/40"
                    >
                      Try again
                    </button>
                  </motion.div>
                )}
                {phase === 'chips' && (
                  <motion.div
                    key="chips"
                    className="flex min-h-8 flex-wrap gap-2"
                    initial="hidden"
                    animate="shown"
                    exit={{ opacity: 0, transition: fade }}
                    variants={{
                      hidden: {},
                      shown: { transition: { staggerChildren: reduceMotion ? 0 : 0.07 } },
                    }}
                  >
                    {alternatives.map((item, index) => (
                      <motion.span
                        key={`${item.alternative}-${index}`}
                        className="inline-flex max-w-full"
                        variants={{
                          hidden: { opacity: 0, y: 6 },
                          shown: {
                            opacity: 1,
                            y: 0,
                            transition: reduceMotion ? { duration: 0 } : { duration: 0.4, ease: ALTERNATIVE_EASE },
                          },
                        }}
                      >
                        <AlternativeChip item={item} />
                      </motion.span>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.section>
      )}
    </AnimatePresence>
  );
}

function AlternativeChip({ item }: { item: MeaningAlternativeCard }) {
  const hasScore = item.score !== undefined && Number.isFinite(item.score);

  return (
    <span className="inline-flex min-h-8 max-w-full items-center gap-2 rounded-full border border-[#d9cbb7] bg-[#fffaf1] px-3 py-1.5 text-sm font-semibold leading-5 text-[#17231f] transition-colors duration-150">
      <span className="min-w-0 break-words">{item.alternative}</span>
      {hasScore && (
        <span className="shrink-0 border-l border-[#d9cbb7] pl-2 text-xs font-semibold tabular-nums text-[#496653]">
          {item.score}/10
        </span>
      )}
    </span>
  );
}

function TagChip({
  tag,
  onRemove,
}: {
  tag: MeaningTagCard;
  onRemove?: () => void;
}) {
  return (
    <span className={cn('inline-flex h-7 items-center gap-1 rounded-full px-3 text-xs font-medium transition', getTagColor(tag.name))}>
      {tag.name}
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${tag.name} tag`}
          onClick={onRemove}
          className="relative ml-0.5 rounded-full p-0.5 opacity-60 transition before:absolute before:-inset-[15px] before:content-[''] hover:bg-white/50 hover:opacity-100"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}

function TagPickerDialog({
  open,
  tags,
  selectedTags,
  saving,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  tags: MeaningTagCard[];
  selectedTags: MeaningTagCard[];
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (tagIds: string[], newTagNames: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [newTagNames, setNewTagNames] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIds(selectedTags.map((tag) => tag.id));
      setNewTagNames([]);
    }
  }, [open, selectedTags]);

  const trimmedQuery = query.trim();
  const exactTag = tags.find((tag) => tag.name.toLowerCase() === trimmedQuery.toLowerCase());
  const filteredTags = tags.filter((tag) => tag.name.toLowerCase().includes(trimmedQuery.toLowerCase()));
  const canCreate = Boolean(
    trimmedQuery &&
    trimmedQuery.length <= 50 &&
    !exactTag &&
    !newTagNames.some((name) => name.toLowerCase() === trimmedQuery.toLowerCase())
  );

  const addNewTag = () => {
    if (!canCreate) return;
    setNewTagNames((current) => [...current, trimmedQuery]);
    setQuery('');
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !saving && onOpenChange(nextOpen)}>
      <DialogContent className="gap-5 rounded-2xl border-white bg-white">
        <DialogHeader>
          <DialogTitle>Manage tags</DialogTitle>
          <DialogDescription>Select existing tags or create a new one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canCreate) {
                  event.preventDefault();
                  addNewTag();
                }
              }}
              placeholder="Search or create a tag"
              maxLength={50}
              className="h-11 rounded-xl border-slate-200 pl-10"
            />
          </div>

          {newTagNames.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {newTagNames.map((name) => (
                <TagChip
                  key={name}
                  tag={{ id: name, name }}
                  onRemove={() => setNewTagNames((current) => current.filter((item) => item !== name))}
                />
              ))}
            </div>
          )}

          <div className="max-h-60 space-y-1 overflow-y-auto pr-1">
            {canCreate && (
              <button
                type="button"
                onClick={addNewTag}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50"
              >
                <Plus className="size-4 text-slate-400" />
                Create “{trimmedQuery}”
              </button>
            )}
            {filteredTags.map((tag) => {
              const selected = selectedIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => setSelectedIds((current) =>
                    selected ? current.filter((id) => id !== tag.id) : [...current, tag.id]
                  )}
                  className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition hover:bg-slate-50"
                >
                  <span className="flex items-center gap-2">
                    <Tag className="size-4 text-slate-400" />
                    {tag.name}
                  </span>
                  <span className={cn(
                    'flex size-5 items-center justify-center rounded-full border transition',
                    selected ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200'
                  )}>
                    {selected && <Check className="size-3" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={saving}
            onClick={() => onSave(selectedIds, newTagNames)}
            className="bg-slate-950 text-white hover:bg-slate-800"
          >
            {saving && <LoaderCircle className="size-4 animate-spin" />}
            Apply tags
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MeaningPreviewCard({
  card,
  layoutId,
  onOpen,
}: {
  card: MeaningCard;
  layoutId: string;
  onOpen: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const inner = innerRef.current;
    if (!content || !inner) return;

    const checkOverflow = () => setOverflowing(inner.scrollHeight > content.clientHeight + 1);

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(content);
    resizeObserver.observe(inner);

    return () => resizeObserver.disconnect();
  }, [card]);

  return (
    <motion.article
      layoutId={layoutId}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      <Card
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen();
          }
        }}
        className="max-h-[288px] overflow-hidden gap-0 rounded-[14px] border-[#dedede] bg-white py-4 shadow-[0_1px_2px_rgba(15,23,42,0.08),0_1px_8px_rgba(15,23,42,0.08)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_3px_12px_rgba(15,23,42,0.12)] focus-visible:ring-3 focus-visible:ring-primary/25"
      >
        <CardHeader className="shrink-0 px-5 pb-2">
          <CardTitle
            onClick={(event) => event.stopPropagation()}
            className="cursor-text select-text font-display text-[21px] font-semibold leading-7 tracking-[-0.005em] text-black"
          >
            <HighlightedSlots text={card.phrase} active={card.isSlotted} />
          </CardTitle>
        </CardHeader>
        <CardContent
          ref={contentRef}
          className={cn(
            'relative flex-1 overflow-hidden px-5 pb-0',
            overflowing &&
              'after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-8 after:bg-gradient-to-b after:from-white/0 after:to-white'
          )}
        >
          <div ref={innerRef}>
            <MeaningDetails card={card} compact />
          </div>
        </CardContent>
      </Card>
    </motion.article>
  );
}

function CardOverflowMenu({
  phrase,
  youglishUrl,
  regenerating,
  disabled,
  onRegenerate,
  onDeleteRequest,
}: {
  phrase: string;
  youglishUrl: string;
  regenerating: boolean;
  disabled: boolean;
  onRegenerate: () => void;
  onDeleteRequest: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex">
      <button
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex w-9 items-center justify-center rounded-r-lg text-[#57534a] transition enabled:hover:bg-[#f6f4ef] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span aria-hidden className="text-lg font-bold leading-none tracking-widest">&#8943;</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Card actions"
          className="absolute right-0 top-[calc(100%+6px)] z-30 w-60 overflow-hidden rounded-xl border border-[#e6e2da] bg-white p-1.5 shadow-[0_12px_28px_rgba(28,34,51,0.14)]"
        >
          <button
            type="button"
            role="menuitem"
            disabled={regenerating}
            onClick={() => {
              setOpen(false);
              onRegenerate();
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#3d3a33] transition enabled:hover:bg-[#f6f4ef] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {regenerating ? (
              <LoaderCircle className="size-4 shrink-0 animate-spin text-[#a39c8d]" />
            ) : (
              <RefreshCw className="size-4 shrink-0 text-[#a39c8d]" />
            )}
            Regenerate
          </button>
          <a
            href={youglishUrl}
            target="_blank"
            rel="noreferrer"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#3d3a33] transition hover:bg-[#f6f4ef]"
          >
            <ExternalLink className="size-4 shrink-0 text-[#a39c8d]" />
            <span className="truncate">Open “{phrase}” on YouGlish</span>
          </a>
          <div aria-hidden className="mx-1 my-1.5 h-px bg-[#efece5]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDeleteRequest();
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-600 transition hover:bg-red-50"
          >
            <Trash2 className="size-4 shrink-0" />
            Delete card
          </button>
        </div>
      )}
    </div>
  );
}

const sidePanelShellEase = [0.22, 1, 0.36, 1] as const;

const sidePanelTabClassName = (selected: boolean) =>
  cn(
    'flex items-center gap-1 whitespace-nowrap border-b-2 pt-1 text-sm font-semibold transition-colors',
    selected ? 'border-[#1c2230] text-[#1c2230]' : 'border-transparent text-[#6b6b63] hover:text-[#1c2230]'
  );

function CardSidePanelShell({
  activePanel,
  wide,
  phrase,
  onSelectLive,
  onSelectVideos,
  onAskAI,
  onToggleWide,
  onClose,
  children,
}: {
  activePanel: CardSidePanel;
  wide: boolean;
  phrase: string;
  onSelectLive: () => void;
  onSelectVideos: () => void;
  onAskAI: () => void;
  onToggleWide: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const open = activePanel !== null;
  const panelLabel = activePanel === 'chat' ? `AI chat about ${phrase}` : `Examples for ${phrase}`;
  const tabs: { panel: Exclude<CardSidePanel, null>; select: () => void }[] = [
    { panel: 'feed', select: onSelectLive },
    { panel: 'videos', select: onSelectVideos },
    { panel: 'chat', select: onAskAI },
  ];

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const current = tabs.findIndex((tab) => tab.panel === activePanel);
    const next = (current + step + tabs.length) % tabs.length;
    tabs[next].select();
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons[next]?.focus();
  };

  return (
    // initial={false}: when the panel is already open on the very first paint (desktop default),
    // it should not play its own slide-in — it appears together with the card's shared-layout grow
    // transition instead of competing with it as a second, differently-timed animation. A panel the
    // user opens or closes later — after this has already mounted — still animates normally.
    <AnimatePresence mode="popLayout" initial={false}>
      {open && (
        <motion.button
          key="side-panel-backdrop"
          type="button"
          aria-label="Close panel"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28 }}
          className="fixed inset-0 z-20 bg-slate-900/20 backdrop-blur-[2px] lg:hidden"
        />
      )}
      {open && (
        <motion.aside
          key="side-panel"
          role="complementary"
          aria-label={panelLabel}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 24 }}
          transition={{ duration: 0.28, ease: sidePanelShellEase }}
          className={cn(
            'fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col p-4 lg:relative lg:inset-auto lg:z-auto lg:w-auto lg:max-w-none lg:min-w-0 lg:self-stretch lg:p-0',
            wide ? 'lg:flex-[3]' : 'lg:flex-[2]'
          )}
        >
          <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-white bg-white shadow-[0_22px_70px_rgba(15,23,42,0.12)] lg:absolute lg:inset-0 lg:shadow-[0_1px_2px_rgba(15,23,42,0.08),0_1px_8px_rgba(15,23,42,0.08)]">
            <header className="flex h-16 shrink-0 items-stretch justify-between gap-3 border-b border-[#ecebe6] pr-3 pl-[22px]">
              <div
                role="tablist"
                aria-label={`Panels for ${phrase}`}
                onKeyDown={handleTabKeyDown}
                className="-mb-px flex min-w-0 items-stretch gap-[22px]"
              >
                <button
                  type="button"
                  role="tab"
                  onClick={onSelectLive}
                  aria-selected={activePanel === 'feed'}
                  tabIndex={activePanel === 'feed' ? 0 : -1}
                  className={sidePanelTabClassName(activePanel === 'feed')}
                >
                  News
                </button>
                <button
                  type="button"
                  role="tab"
                  onClick={onSelectVideos}
                  aria-selected={activePanel === 'videos'}
                  tabIndex={activePanel === 'videos' ? 0 : -1}
                  className={sidePanelTabClassName(activePanel === 'videos')}
                >
                  Videos
                </button>
                <button
                  type="button"
                  role="tab"
                  onClick={onAskAI}
                  aria-selected={activePanel === 'chat'}
                  tabIndex={activePanel === 'chat' ? 0 : -1}
                  className={sidePanelTabClassName(activePanel === 'chat')}
                >
                  <span aria-hidden className="text-[#b45309]">✦</span>
                  Ask AI
                </button>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={wide ? 'Give the card the wider column' : 'Give the panel the wider column'}
                  aria-pressed={wide}
                  onClick={onToggleWide}
                  className={cn(
                    'hidden rounded-lg lg:inline-flex',
                    wide
                      ? 'bg-slate-200 text-slate-800 hover:bg-slate-300 hover:text-slate-900'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                  )}
                >
                  {wide ? <PanelLeft className="size-4" /> : <PanelRight className="size-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close panel"
                  onClick={onClose}
                  className="rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                >
                  <X className="size-4" />
                </Button>
              </div>
            </header>

            <div role="tabpanel" aria-label={panelLabel} className="min-h-0 flex-1">{children}</div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

function ExpandedMeaningCard({
  card,
  allTags,
  error,
  updating,
  regeneratingCard,
  deleting,
  pinning,
  regeneratingExamples,
  savingTags,
  onCardUpdate,
  onCardRegenerate,
  onCardDelete,
  onPin,
  onRegenerate,
  alternativesFailed,
  onAlternativesRetry,
  onTagsUpdate,
  onBack,
  onLayoutSettled,
  backLabel,
}: {
  card: MeaningCard;
  allTags: MeaningTagCard[];
  error: string;
  updating: boolean;
  regeneratingCard: boolean;
  deleting: boolean;
  pinning: string;
  regeneratingExamples: boolean;
  savingTags: boolean;
  onCardUpdate: (value: string) => Promise<boolean>;
  onCardRegenerate: () => Promise<void>;
  onCardDelete: () => Promise<void>;
  onPin: (exampleIndex: number, pinned: boolean) => void;
  onRegenerate: () => void;
  alternativesFailed: boolean;
  onAlternativesRetry: () => void;
  onTagsUpdate: (tagIds: string[], newTagNames: string[]) => Promise<boolean>;
  onBack: () => void;
  onLayoutSettled: () => void;
  backLabel: string;
}) {
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerValue, setHeaderValue] = useState(card.phrase);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [activeSidePanel, setActiveSidePanel] = useState<CardSidePanel>(getDefaultCardSidePanel);
  // Swaps the two columns' flex ratios: the card is normally the wider of the pair, and this hands
  // that width over to the side panel for reading a live match or watching a video full width.
  const [sidePanelWide, setSidePanelWide] = useState(false);
  const [chatQuestion, setChatQuestion] = useState<{ id: number; text: string } | null>(null);
  const { chipStates, setChipState } = usePhraseChips(card.id ?? '');
  const reopenPanelRef = useRef<HTMLButtonElement>(null);
  const feedPanelId = 'card-feed-panel';
  const chatPanelId = 'card-chat-panel';
  const videosPanelId = 'card-videos-panel';
  const canEdit = Boolean(card.id);
  const primaryContext = card.context;
  const youglishUrl = buildYouglishUrl(card.phrase);

  useEffect(() => {
    setHeaderValue(card.phrase);
    setEditingHeader(false);
    setActiveSidePanel(getDefaultCardSidePanel());
    setSidePanelWide(false);
    setChatQuestion(null);
  }, [card.id, card.phrase]);

  const closeSidePanel = () => {
    setActiveSidePanel(null);
    requestAnimationFrame(() => {
      reopenPanelRef.current?.focus();
    });
  };

  useEffect(() => {
    if (!activeSidePanel) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeSidePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSidePanel]);

  const selectLive = () => {
    setActiveSidePanel('feed');
  };

  const selectVideos = () => {
    setActiveSidePanel('videos');
  };

  const askAboutCollocation = (text: string) => {
    setChatQuestion({ id: Date.now(), text });
    setActiveSidePanel('chat');
  };

  const handleHeaderSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextValue = headerValue.trim();
    if (!nextValue || nextValue === card.phrase || updating || regeneratingCard) {
      setHeaderValue(card.phrase);
      setEditingHeader(false);
      return;
    }

    const updated = await onCardUpdate(nextValue);
    if (updated) {
      setEditingHeader(false);
    }
  };

  return (
    <main className="flex flex-1 flex-col px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] text-slate-950 sm:px-6 md:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div
        className={cn(
          'mx-auto flex w-full max-w-2xl flex-1 flex-col lg:max-w-5xl',
          activeSidePanel && 'lg:max-w-[78rem] 2xl:max-w-[96rem]'
        )}
      >
        <div aria-live="polite" className="sr-only">
          {deleting && 'Deleting card…'}
          {regeneratingCard && 'Regenerating card…'}
          {updating && 'Saving changes…'}
        </div>

        <div className="flex flex-1 items-stretch">
        {/* Carries the layoutId for the shared transition with the list row. It now spans BOTH
            columns — not just the definition card — so opening and closing animate the whole
            merged shape as one unit instead of the definition card growing/shrinking on its own
            while the examples panel pops in and out with a separate, uncoordinated motion. */}
        <motion.div
          layoutId={getMeaningLayoutId(card)}
          // Keyed on the card so the page can drop its scroll offset without that turning into a
          // layout animation; content changes still animate because `card` gets a new identity.
          layoutDependency={card}
          onLayoutAnimationComplete={onLayoutSettled}
          // The card carries over from the row it grew out of, so it has to be solid from the
          // first frame. That means opting out of both fades: our own, and the crossfade
          // framer-motion runs between shared elements — the row is already gone by then, so
          // crossfading leaves nothing on screen for the length of the animation.
          layoutCrossfade={false}
          initial={false}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          style={{ willChange: 'transform' }}
          className={cn(
            'flex min-w-0 flex-1 rounded-lg shadow-[0_22px_70px_rgba(15,23,42,0.12)]',
            activeSidePanel && 'lg:gap-3 lg:rounded-none lg:shadow-none'
          )}
        >
          <div
            className={cn(
              'flex min-w-0 flex-1 flex-col',
              activeSidePanel && (sidePanelWide ? 'lg:flex-[2]' : 'lg:flex-[3]'),
              activeSidePanel && 'lg:rounded-lg lg:shadow-[0_1px_2px_rgba(15,23,42,0.08),0_1px_8px_rgba(15,23,42,0.08)]'
            )}
          >
            <header
              className={cn(
                'sticky top-0 z-10 flex h-16 flex-wrap items-center gap-3 rounded-t-lg border-t border-x border-b border-t-white border-x-white border-b-[#f1eee8] bg-white/95 px-4 backdrop-blur sm:px-6'
              )}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon-lg"
                onClick={onBack}
                aria-label="Back to meanings"
                className="rounded-full text-[#3d3a33] hover:bg-[#f6f4ef]"
              >
                <ArrowLeft className="size-5" />
              </Button>
              <p className="text-sm font-medium text-[#7b7669]">{backLabel}</p>
              {canEdit && !editingHeader && (
                <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {!activeSidePanel && (
                    <Button
                      ref={reopenPanelRef}
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Show examples panel"
                      onClick={selectLive}
                      className="rounded-lg text-[#7b7669] hover:bg-[#f6f4ef] hover:text-[#1e1c18]"
                    >
                      <PanelRight className="size-4" />
                    </Button>
                  )}
                  <div className="flex h-9 items-stretch overflow-visible rounded-lg border border-[#e6e2da]">
                    <button
                      type="button"
                      disabled={deleting || regeneratingCard}
                      onClick={() => setEditingHeader(true)}
                      className="flex items-center gap-1.5 rounded-l-lg px-3 text-sm font-semibold text-[#3d3a33] transition hover:bg-[#f6f4ef] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Pencil className="size-4" />
                      Edit
                    </button>
                    <div aria-hidden className="w-px bg-[#e6e2da]" />
                    <CardOverflowMenu
                      phrase={card.phrase}
                      youglishUrl={youglishUrl}
                      regenerating={regeneratingCard}
                      disabled={
                        deleting ||
                        updating ||
                        regeneratingCard ||
                        regeneratingExamples ||
                        Boolean(pinning) ||
                        savingTags
                      }
                      onRegenerate={onCardRegenerate}
                      onDeleteRequest={() => setDeleteConfirmOpen(true)}
                    />
                  </div>
                </div>
              )}
            </header>

            <Dialog open={deleteConfirmOpen} onOpenChange={(nextOpen) => !deleting && setDeleteConfirmOpen(nextOpen)}>
              <DialogContent className="gap-5 rounded-2xl border-white bg-white">
                <DialogHeader>
                  <DialogTitle>Delete “{card.phrase}”?</DialogTitle>
                  <DialogDescription>
                    Its sentence, examples, and review history are removed. This cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button type="button" variant="ghost" disabled={deleting} onClick={() => setDeleteConfirmOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={deleting}
                    onClick={() => {
                      setDeleteConfirmOpen(false);
                      void onCardDelete();
                    }}
                  >
                    {deleting && <LoaderCircle className="size-4 animate-spin" />}
                    Delete card
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <article
              className={cn(
                'relative flex min-w-0 flex-1 flex-col rounded-b-lg border border-t-0 border-white bg-white px-6 py-8 sm:px-9 sm:py-10'
              )}
            >
          {editingHeader ? (
            <form onSubmit={handleHeaderSubmit} className="flex items-center gap-2">
              <label htmlFor="meaning-header" className="sr-only">
                Edit card header
              </label>
              <Input
                id="meaning-header"
                value={headerValue}
                onChange={(event) => setHeaderValue(event.target.value)}
                autoFocus
                disabled={updating || regeneratingCard}
                className={cn(
                  'h-14 flex-1 rounded-lg border-[#e6e2da] px-3 font-display font-semibold tracking-[-0.01em] text-[#1e1c18] shadow-none focus-visible:border-[#2f5c4e] focus-visible:ring-0',
                  getMeaningTitleSize(headerValue)
                )}
              />
              <Button
                type="submit"
                size="icon-lg"
                aria-label="Save card header"
                disabled={updating || regeneratingCard || !headerValue.trim()}
                className="shrink-0 rounded-full bg-[#2f5c4e] text-white hover:bg-[#26493d]"
              >
                {updating ? <LoaderCircle className="size-5 animate-spin" /> : <Check className="size-5" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-lg"
                aria-label="Cancel card header edit"
                disabled={updating || regeneratingCard}
                onClick={() => {
                  setHeaderValue(card.phrase);
                  setEditingHeader(false);
                }}
                className="shrink-0 rounded-full text-[#57534a] hover:bg-[#f6f4ef]"
              >
                <X className="size-5" />
              </Button>
            </form>
          ) : (
            <div className={cn('min-w-0 font-display', getMeaningTitleSize(card.phrase))}>
              {/* The title's font metrics live on this wrapper, not on the h1: `align-middle`
                  on the button resolves against its PARENT's x-height, so the parent has to
                  carry the display font at title size for the button to land on the
                  lowercase band. */}
              <h1 className="inline break-words font-semibold tracking-[-0.01em] text-black">
                <HighlightedSlots text={card.phrase} active={card.isSlotted} />
              </h1>
              <span className="inline-flex align-middle">
                <SpeakButton
                  text={card.phrase}
                  sound={card.sound}
                  variant="ghost"
                  size="icon"
                  className="ml-2 inline-flex rounded-full text-[#57534a] hover:bg-[#e7f0e8] hover:text-[#2f5c4e]"
                />
              </span>
            </div>
          )}
          {primaryContext && (
            <div className="mt-4 border-l-2 border-amber-400 pl-4 text-[15px] italic leading-8 text-[#57534a]">
              <HighlightedSelections text={primaryContext} selections={card.selections} />
            </div>
          )}
          {canEdit && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {card.tags.map((tag) => (
                <TagChip
                  key={tag.id}
                  tag={tag}
                  onRemove={regeneratingCard ? undefined : () => onTagsUpdate(
                    card.tags.filter((cardTag) => cardTag.id !== tag.id).map((cardTag) => cardTag.id),
                    []
                  )}
                />
              ))}
              <button
                type="button"
                aria-label="Add tag"
                disabled={savingTags || regeneratingCard}
                onClick={() => setTagPickerOpen(true)}
                className={cn(
                  'flex h-7 items-center justify-center gap-1 rounded-full bg-[#f3f1ec] text-xs font-medium text-[#57534a] transition hover:bg-[#e6e2da] hover:text-[#1e1c18] disabled:opacity-50',
                  card.tags.length > 0 ? 'w-7' : 'px-3'
                )}
              >
                {savingTags ? <LoaderCircle className="size-3.5 animate-spin" /> : <Plus className="size-4" />}
                {card.tags.length === 0 && <span>Add tag</span>}
              </button>
            </div>
          )}
          <div className="mt-8 w-full">
            <MeaningDetails
              card={card}
              pinning={pinning}
              regeneratingCard={regeneratingCard}
              regeneratingExamples={regeneratingExamples}
              onPin={onPin}
              onRegenerate={onRegenerate}
            />
            <MeaningAlternatives
              alternatives={card.alternatives}
              pending={Boolean(card.alternativesPending) && !alternativesFailed}
              failed={alternativesFailed}
              onRetry={onAlternativesRetry}
            />
            {card.id && (
              <CardCollocations
                meaningId={card.id}
                word={card.canonical || card.phrase}
                onAsk={askAboutCollocation}
              />
            )}
          </div>
          {error && (
            <div role="alert" className="mt-6 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}
            </article>
          </div>

          {card.id && (
            <CardSidePanelShell
              activePanel={activeSidePanel}
              wide={sidePanelWide}
              phrase={card.phrase}
              onSelectLive={selectLive}
              onSelectVideos={selectVideos}
              onAskAI={() => setActiveSidePanel('chat')}
              onToggleWide={() => setSidePanelWide((wide) => !wide)}
              onClose={closeSidePanel}
            >
              <CardFeedPanel
                meaningId={card.id}
                open={activeSidePanel === 'feed'}
                panelId={feedPanelId}
                initialChips={card.phraseChips}
                phraseTokens={card.phraseTokens}
                chipStates={chipStates}
                onChipChange={setChipState}
              />
              <CardChatPanel
                meaningId={card.id}
                term={card.phrase}
                alternatives={card.alternatives.map((item) => item.alternative)}
                open={activeSidePanel === 'chat'}
                panelId={chatPanelId}
                pendingQuestion={chatQuestion}
              />
              <CardVideosPanel
                meaningId={card.id}
                term={card.phrase}
                initialChips={card.phraseChips}
                phraseTokens={card.phraseTokens}
                open={activeSidePanel === 'videos'}
                panelId={videosPanelId}
              />
            </CardSidePanelShell>
          )}
        </motion.div>
        </div>
      </div>
      <TagPickerDialog
        open={tagPickerOpen}
        tags={allTags}
        selectedTags={card.tags}
        saving={savingTags}
        onOpenChange={setTagPickerOpen}
        onSave={async (tagIds, newTagNames) => {
          if (await onTagsUpdate(tagIds, newTagNames)) {
            setTagPickerOpen(false);
          }
        }}
      />
    </main>
  );
}

function MeaningDetailStateCard({
  onBack,
  backLabel,
  error,
  onRetry,
}: {
  onBack: () => void;
  backLabel: string;
  error?: string;
  onRetry?: () => void;
}) {
  return (
    <main className="flex flex-1 flex-col px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] text-slate-950 sm:px-6 md:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col lg:max-w-5xl">
        <div className="flex flex-1 flex-col rounded-lg shadow-[0_22px_70px_rgba(15,23,42,0.12)]">
          <header className="sticky top-0 z-10 flex items-center gap-3 rounded-t-lg border border-b-0 border-white bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
            <Button type="button" variant="ghost" size="icon-lg" onClick={onBack} aria-label="Back" className="rounded-full text-slate-700 hover:bg-slate-100">
              <ArrowLeft className="size-5" />
            </Button>
            <p className="text-sm font-medium text-slate-500">{backLabel}</p>
          </header>

          <article
            aria-busy={!error}
            className="flex flex-1 items-center justify-center rounded-b-lg border border-t-0 border-white bg-white px-6 py-8 sm:px-9 sm:py-10"
          >
            {error ? (
              <div role="alert" className="flex max-w-sm flex-col items-center gap-4 text-center">
                <p className="text-sm leading-6 text-slate-600">{error}</p>
                <Button type="button" variant="outline" onClick={onRetry}>Retry</Button>
              </div>
            ) : (
              <>
                <LoaderCircle className="size-7 animate-spin text-slate-500" aria-hidden="true" />
                <span className="sr-only">Loading card</span>
              </>
            )}
          </article>
        </div>
      </div>
    </main>
  );
}

/**
 * Desktop chrome for a single opened card: header and the This week/Tags column stay put (same as
 * the list), and the feed + Live mentions area is replaced by the card itself — whose own side
 * panel (Live/Videos for that one card) takes over where the global Live mentions widget was.
 */
function MeaningDesktopDetailShell({
  allTags,
  selectedTagId,
  onSelectTag,
  onCreateTag,
  onManageTags,
  totalTagCount,
  totalCardCount,
  tagStatisticsLoading,
  tagStatisticsError,
  onRetryTagStatistics,
  weeklyStatistics,
  weeklyStatisticsLoading,
  weeklyStatisticsError,
  onRetryWeeklyStatistics,
  children,
}: {
  allTags: MeaningTagCard[];
  selectedTagId: string | null;
  onSelectTag: (tagId: string | null) => void;
  onCreateTag: (name: string) => Promise<CreateSidebarTagResult>;
  onManageTags: () => void;
  totalTagCount: number;
  totalCardCount: number;
  tagStatisticsLoading: boolean;
  tagStatisticsError: string;
  onRetryTagStatistics: () => void;
  weeklyStatistics: WeeklyStatistics | null;
  weeklyStatisticsLoading: boolean;
  weeklyStatisticsError: string;
  onRetryWeeklyStatistics: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-[#f6f4ef]">
      {/* Header keeps the list's exact frame (1364px, unchanged) — its logo/nav/search/account stay
          exactly where they are in the list; only the row below gets the wider, still-centered
          frame, so opening a card doesn't move anything in the header at all. */}
      <AppTopHeader />
      {/* Left edge keeps the same offset the list's centered 1600px frame would give it (so This
          week/Tags doesn't move — see layoutGrid.ts for the reference width), via padding-left
          instead of mx-auto so the right side can be pinned independently: 16px padding-right plus
          the card's own 24px inner padding (sm:px-6 on ExpandedMeaningCard's <main>) puts the
          card's visible right edge a fixed 40px from the true screen edge — the same 40px gap the
          card has from the Tags panel on the left — rather than a gap that grows with the window.
          The aside is a fixed 348px (same width as the list, not a share of the total) so only its
          position shifts, never its width. No items-start here (unlike the list row) — the card
          column stretches to fill the row's full height (at least one viewport tall, via flex-1
          inside the shell's min-h-dvh) instead of stopping at its own content height and leaving
          the shell's bare background showing below it. The aside opts back out of that stretch with
          its own self-start, so it keeps behaving exactly like it does in the list — sticky,
          capped, independently scrollable. layoutId ties it to the list's aside so framer-motion
          animates the position change as a small slide instead of a hard jump when a card opens or
          closes. */}
      <div
        className="grid w-full flex-1 grid-cols-[348px_minmax(0,1fr)]"
        style={{ paddingLeft: `max(0px, calc((100% - ${MEANING_DETAIL_LEFT_REFERENCE_WIDTH}px) / 2))`, paddingRight: '16px' }}
      >
        <motion.aside
          layoutId={MEANING_LEFT_RAIL_LAYOUT_ID}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="sticky top-[66px] flex min-w-0 max-h-[calc(100dvh-66px)] flex-col gap-3.5 self-start overflow-y-auto p-4"
        >
          <ThisWeekWidget
            statistics={weeklyStatistics}
            loading={weeklyStatisticsLoading}
            error={weeklyStatisticsError}
            onRetry={onRetryWeeklyStatistics}
          />
          <TagsSidebarList
            tags={allTags}
            selectedTagId={selectedTagId}
            onSelect={onSelectTag}
            onCreate={onCreateTag}
            onManage={onManageTags}
            totalTagCount={totalTagCount}
            totalCardCount={totalCardCount}
            loading={tagStatisticsLoading}
            error={tagStatisticsError}
            onRetry={onRetryTagStatistics}
          />
        </motion.aside>
        <div className="flex min-w-0 flex-col">{children}</div>
      </div>
    </div>
  );
}

export default function MeaningPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { wordSlug, meaningId } = useParams();
  const isDesktopLayout = useIsDesktopLayout();
  const [meanings, setMeanings] = useState<MeaningCard[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoadState, setDetailLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    () => meaningId ? 'loading' : 'idle'
  );
  const [detailLoadError, setDetailLoadError] = useState('');
  const [detailLoadAttempt, setDetailLoadAttempt] = useState(0);
  const [detailCard, setDetailCard] = useState<MeaningCard | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [weeklyStatistics, setWeeklyStatistics] = useState<WeeklyStatistics | null>(null);
  const [weeklyStatisticsLoading, setWeeklyStatisticsLoading] = useState(true);
  const [weeklyStatisticsError, setWeeklyStatisticsError] = useState('');
  const [weeklyStatisticsAttempt, setWeeklyStatisticsAttempt] = useState(0);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [regeneratingCard, setRegeneratingCard] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pinning, setPinning] = useState('');
  const [regeneratingExamples, setRegeneratingExamples] = useState(false);
  const [failedAlternatives, setFailedAlternatives] = useState<Record<string, boolean>>({});
  const alternativesInFlight = useRef(new Set<string>());
  const [allTags, setAllTags] = useState<MeaningTagCard[]>([]);
  const [totalCardCount, setTotalCardCount] = useState(0);
  const [totalTagCount, setTotalTagCount] = useState(0);
  const [tagStatisticsLoading, setTagStatisticsLoading] = useState(true);
  const [tagStatisticsError, setTagStatisticsError] = useState('');
  const [tagStatisticsAttempt, setTagStatisticsAttempt] = useState(0);
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [savingTags, setSavingTags] = useState(false);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [createDialog, setCreateDialog] = useState<'contextual' | 'manual' | null>(null);
  const [createError, setCreateError] = useState('');
  const searchQuery = debouncedSearch.trim();
  const meaningsRef = useRef<MeaningCard[]>(meanings);
  const reduceMotion = useReducedMotion();
  // The shared-element transition between a list card and the detail view is measured by
  // framer-motion in page coordinates. Jumping the window scroll in the same commit therefore
  // makes the card fly in from `scrollY` px below (or above) where the user actually sees it.
  // Instead the scroll stays where it is for the length of the animation and the whole app shell
  // is nudged down by that amount, so the page still looks like its own top; offset and scroll are
  // then dropped together in one frame. Tracked in a ref, not in state: every change is paired
  // with the matching DOM write, so the frames where the transition starts and ends need no render.
  const detailScrollOffsetRef = useRef(0);
  const settleTimeoutRef = useRef(0);
  const scrollHandledManually = useRef(false);
  // Mobile and desktop layouts are both mounted at once (toggled with `md:hidden`/`hidden md:flex`),
  // so each needs its own sentinel — only the one with layout (i.e. actually visible) ever intersects.
  const listVersionRef = useRef(0);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [mobileLoadMoreTrigger, setMobileLoadMoreTrigger] = useState<HTMLDivElement | null>(null);
  const [desktopLoadMoreTrigger, setDesktopLoadMoreTrigger] = useState<HTMLDivElement | null>(null);

  useEffect(() => () => window.clearTimeout(settleTimeoutRef.current), []);

  useEffect(() => {
    meaningsRef.current = meanings;
  }, [meanings]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    let mounted = true;
    listVersionRef.current += 1;

    const loadMeanings = async () => {
      try {
        setLoading(true);
        setLoadingMore(false);
        setLoadMoreFailed(false);
        setError('');
        setMeanings([]);
        setNextCursor(null);
        setHasMore(false);
        const page = await apiClient.getPagedMeanings(null, 20, selectedTagId, searchQuery);
        if (mounted) {
          setMeanings(getMeaningPageItems(page).map(normalizeMeaning));
          setNextCursor(getMeaningPageNextCursor(page));
          setHasMore(getMeaningPageHasMore(page));
        }
      } catch {
        if (mounted) {
          setError('Unable to load meanings.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadMeanings();

    return () => {
      mounted = false;
    };
  }, [searchQuery, selectedTagId, retryCount]);

  useEffect(() => {
    let mounted = true;
    setTagStatisticsLoading(true);
    setTagStatisticsError('');

    apiClient.getTagStatistics()
      .then((statistics) => {
        if (!mounted) return;
        const normalized = normalizeTagStatistics(statistics);
        setAllTags(normalized.tags);
        setTotalCardCount(normalized.totalCardCount);
        setTotalTagCount(normalized.totalTagCount);
      })
      .catch(() => {
        if (mounted) setTagStatisticsError('Unable to load tags.');
      })
      .finally(() => {
        if (mounted) setTagStatisticsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [tagStatisticsAttempt]);

  useEffect(() => {
    let mounted = true;

    if (!isDesktopLayout) {
      return () => {
        mounted = false;
      };
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!timeZone) {
      setWeeklyStatistics(null);
      setWeeklyStatisticsLoading(false);
      setWeeklyStatisticsError('Unable to determine your time zone.');
      return () => {
        mounted = false;
      };
    }

    setWeeklyStatisticsLoading(true);
    setWeeklyStatisticsError('');

    apiClient.getWeeklyStatistics(timeZone)
      .then((statistics) => {
        if (mounted) setWeeklyStatistics(statistics);
      })
      .catch(() => {
        if (mounted) {
          setWeeklyStatistics(null);
          setWeeklyStatisticsError('Unable to load weekly statistics.');
        }
      })
      .finally(() => {
        if (mounted) setWeeklyStatisticsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isDesktopLayout, weeklyStatisticsAttempt]);

  useEffect(() => {
    if (!meaningId) {
      setDetailLoadState('idle');
      setDetailLoadError('');
      setDetailCard(null);
      return;
    }

    let mounted = true;

    const cached = meaningsRef.current.find((card) => card.id === meaningId);
    if (cached) {
      setDetailLoadError('');
      setDetailCard(cached);
      setDetailLoadState('loaded');

      if (detailLoadAttempt === 0) {
        return () => {
          mounted = false;
        };
      }
    }

    const loadMeaning = async () => {
      try {
        if (!cached) {
          setDetailLoadState('loading');
          setDetailLoadError('');
          setDetailCard(null);
        }
        const card = normalizeMeaning(await apiClient.getMeaningById(meaningId));
        if (mounted) {
          setMeanings((current) => appendUniqueMeanings(current, [card]));
          setDetailCard(card);
          setDetailLoadState('loaded');
        }
      } catch {
        if (mounted) {
          if (!cached) {
            setDetailLoadError('Unable to load this card. Please try again.');
            setDetailLoadState('error');
          }
        }
      }
    };

    void loadMeaning();

    return () => {
      mounted = false;
    };
  }, [detailLoadAttempt, meaningId]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !nextCursor) return;

    const version = listVersionRef.current;

    try {
      setLoadingMore(true);
      setLoadMoreFailed(false);
      setError('');
      const page = await apiClient.getPagedMeanings(nextCursor, 20, selectedTagId, searchQuery);
      if (version !== listVersionRef.current) return;
      const nextMeanings = getMeaningPageItems(page).map(normalizeMeaning);
      setMeanings((current) => appendUniqueMeanings(current, nextMeanings));
      setNextCursor(getMeaningPageNextCursor(page));
      setHasMore(getMeaningPageHasMore(page));
    } catch {
      if (version === listVersionRef.current) {
        setLoadMoreFailed(true);
        setError('Unable to load more meanings.');
      }
    } finally {
      if (version === listVersionRef.current) {
        setLoadingMore(false);
      }
    }
  }, [loadingMore, hasMore, nextCursor, selectedTagId, searchQuery]);

  useEffect(() => {
    if (loading || loadingMore || !hasMore || loadMoreFailed) return;

    const triggers = [mobileLoadMoreTrigger, desktopLoadMoreTrigger].filter(
      (trigger): trigger is HTMLDivElement => trigger !== null
    );
    if (triggers.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void handleLoadMore();
        }
      },
      { rootMargin: '0px 0px 400px' }
    );

    triggers.forEach((trigger) => observer.observe(trigger));
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadMoreFailed, handleLoadMore, mobileLoadMoreTrigger, desktopLoadMoreTrigger]);

  const selectedCard = useMemo(() => {
    if (meaningId) {
      return meanings.find((card) => card.id === meaningId) ?? (detailCard?.id === meaningId ? detailCard : undefined);
    }

    if (!wordSlug) return undefined;

    return meanings.find((card) => slugify(card.phrase) === wordSlug);
  }, [detailCard, meaningId, meanings, wordSlug]);

  const loadAlternatives = useCallback(async (cardId: string) => {
    if (alternativesInFlight.current.has(cardId)) return;

    alternativesInFlight.current.add(cardId);
    setFailedAlternatives((current) => {
      if (!current[cardId]) return current;
      const { [cardId]: _, ...rest } = current;
      return rest;
    });

    try {
      const alternatives = normalizeAlternatives(await apiClient.generateMeaningAlternatives(cardId));
      const patch = (card: MeaningCard) =>
        card.id === cardId ? { ...card, alternatives, alternativesPending: false } : card;
      setMeanings((current) => current.map(patch));
      setDetailCard((current) => (current ? patch(current) : current));
    } catch {
      setFailedAlternatives((current) => ({ ...current, [cardId]: true }));
    } finally {
      alternativesInFlight.current.delete(cardId);
    }
  }, []);

  // Cards are created (and regenerated) without alternatives; they are fetched as soon as such a card
  // is opened. The endpoint is idempotent, so a stale pending flag only costs a cheap round trip.
  const selectedCardId = selectedCard?.id;
  const selectedCardAlternativesPending = Boolean(selectedCard?.alternativesPending);
  useEffect(() => {
    if (!selectedCardId || !selectedCardAlternativesPending || failedAlternatives[selectedCardId]) return;
    void loadAlternatives(selectedCardId);
  }, [failedAlternatives, loadAlternatives, selectedCardAlternativesPending, selectedCardId]);

  useLayoutEffect(() => {
    // The scroll position was already put where the transition needs it, keep it.
    const scrollAlreadyPositioned = scrollHandledManually.current;
    scrollHandledManually.current = false;

    // Back on the list the shell owns its own scroll again. This runs after the detail view has
    // been measured for the closing animation, but before the browser paints the list.
    if (!wordSlug && !meaningId) {
      window.clearTimeout(settleTimeoutRef.current);
      detailScrollOffsetRef.current = 0;
      setAppShellOffset(0);
      releaseDocumentScrollRoom();
    }

    // The shell reads the offset while it re-renders for the navigation, but the chrome must never
    // wait on that: writing it here as well guarantees navbar and sidebar are in place in the very
    // first painted frame of the detail view, whatever else re-rendered.
    if ((wordSlug || meaningId) && detailScrollOffsetRef.current) {
      setAppShellOffset(detailScrollOffsetRef.current);
    }

    if (scrollAlreadyPositioned) {
      if (!wordSlug && !meaningId) {
        sessionStorage.removeItem(meaningScrollPositionKey);
      }
      return;
    }

    if (wordSlug || meaningId) {
      window.scrollTo({ left: 0, top: 0 });
      return;
    }

    if (loading) {
      return;
    }

    const savedScrollTop = sessionStorage.getItem(meaningScrollPositionKey);
    if (!savedScrollTop) {
      return;
    }

    requestAnimationFrame(() => {
      window.scrollTo({ left: 0, top: Number(savedScrollTop) });
      sessionStorage.removeItem(meaningScrollPositionKey);
    });
  }, [meanings.length, loading, meaningId, wordSlug]);

  const openCard = (card: MeaningCard) => {
    const path = getMeaningCardPath(card);

    if (!path) {
      return;
    }

    const scrollTop = window.scrollY;
    sessionStorage.setItem(meaningScrollPositionKey, String(scrollTop));

    const offset = reduceMotion ? 0 : scrollTop;
    detailScrollOffsetRef.current = offset;
    if (offset > 0) {
      // The shell picks this up while it re-renders for the navigation, which lands after the list
      // card has been snapshotted at its real page position and before the detail view is measured.
      appShellScrollFrame.offset = offset;
      reserveDocumentScrollRoom(offset);
      scrollHandledManually.current = true;
      // Safety net in case the expand animation never reports back.
      window.clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = window.setTimeout(settleDetailScrollFrame, 800);
    }

    navigate(path);
  };

  const applyMeaningCreationResponse = async (
    response: MeaningCreationResponse,
    tagsToApply?: { tagIds: string[]; newTagNames: string[] }
  ) => {
    const meaning = getCreatedMeaning(response);
    if (!meaning) {
      throw new Error('Meaning creation response did not include a meaning.');
    }

    let created = normalizeMeaning(meaning);

    if (created.id && tagsToApply && (tagsToApply.tagIds.length > 0 || tagsToApply.newTagNames.length > 0)) {
      try {
        const updatedTags = normalizeTags(
          await apiClient.replaceMeaningTags(created.id, tagsToApply.tagIds, tagsToApply.newTagNames)
        );
        created = { ...created, tags: updatedTags };
      } catch {
        // Best-effort: the card was created successfully, tagging it is secondary and shouldn't
        // block opening it.
      }
    }

    setMeanings((current) => {
      const createdKey = getMeaningCardKey(created);
      const withoutDuplicate = current.filter((card) => getMeaningCardKey(card) !== createdKey);
      return [created, ...withoutDuplicate];
    });
    setCreateDialog(null);
    setCreateError('');
    setWeeklyStatisticsAttempt((attempt) => attempt + 1);
    setTagStatisticsAttempt((attempt) => attempt + 1);
    navigate(getMeaningCardPath(created));
  };

  const handleContextualSubmit = async (
    text: string,
    selections: SelectionRange[],
    tags?: { tagIds: string[]; newTagNames: string[] }
  ) => {
    if (!text.trim() || creating) return;

    try {
      setCreating(true);
      setCreateError('');
      await applyMeaningCreationResponse(await apiClient.createMeaning(text, selections), tags);
    } catch {
      setCreateError('Unable to create meaning.');
    } finally {
      setCreating(false);
    }
  };

  const handleManualCreate = async (input: { word: string; translation?: string; definition?: string }) => {
    if (creating) return;

    try {
      setCreating(true);
      setCreateError('');
      await applyMeaningCreationResponse(
        await apiClient.createManualMeaning({
          textInput: input.word,
          translation: input.translation,
          definition: input.definition,
        }));
    } catch {
      setCreateError('Unable to create the manual card.');
    } finally {
      setCreating(false);
    }
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateDialog(null);
    setCreateError('');
  };

  const handleCardUpdate = async (text: string) => {
    if (!selectedCard?.id || updating || regeneratingCard) return false;

    try {
      setUpdating(true);
      setError('');
      const updated = normalizeMeaning(await apiClient.updateMeaning(selectedCard.id, text));

      setMeanings((current) =>
        current.map((card) =>
          card.id === selectedCard.id
            ? updated
            : card
        )
      );
      navigate(getMeaningCardPath(updated), { replace: true });
      return true;
    } catch {
      setError('Unable to update meaning.');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const handleCardDelete = async () => {
    if (!selectedCard?.id || deleting || regeneratingCard) return;

    try {
      setDeleting(true);
      setError('');
      await apiClient.deleteMeaning(selectedCard.id);
      setMeanings((current) => current.filter((card) => card.id !== selectedCard.id));
      setWeeklyStatisticsAttempt((attempt) => attempt + 1);
      setTagStatisticsAttempt((attempt) => attempt + 1);
      navigate('/meaning', { replace: true });
    } catch {
      setError('Unable to delete meaning.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCardRegenerate = async () => {
    if (
      !selectedCard?.id ||
      regeneratingCard ||
      updating ||
      deleting ||
      regeneratingExamples ||
      Boolean(pinning) ||
      savingTags
    ) return;

    try {
      setRegeneratingCard(true);
      setError('');
      const regenerated = normalizeMeaning(await apiClient.regenerateMeaning(selectedCard.id));
      setMeanings((current) => current.map((card) =>
        card.id === selectedCard.id
          ? regenerated
          : card
      ));
      navigate(getMeaningCardPath(regenerated), { replace: true });
    } catch {
      setError('Unable to regenerate meaning.');
    } finally {
      setRegeneratingCard(false);
    }
  };

  const handleTagsUpdate = async (tagIds: string[], newTagNames: string[]) => {
    if (!selectedCard?.id || savingTags || regeneratingCard) return false;

    try {
      setSavingTags(true);
      setError('');
      const updatedTags = normalizeTags(await apiClient.replaceMeaningTags(selectedCard.id, tagIds, newTagNames));
      setMeanings((current) => current.map((card) =>
        card.id === selectedCard.id ? { ...card, tags: updatedTags } : card
      ));
      setAllTags((current) => {
        const byId = new Map(current.map((tag) => [tag.id, tag]));
        updatedTags.forEach((tag) => byId.set(tag.id, tag));
        return Array.from(byId.values()).sort((left, right) => left.name.localeCompare(right.name));
      });
      setTagStatisticsAttempt((attempt) => attempt + 1);
      return true;
    } catch {
      setError('Unable to update tags.');
      return false;
    } finally {
      setSavingTags(false);
    }
  };

  const handleTagCreate = async (name: string): Promise<CreateSidebarTagResult> => {
    try {
      const created = await apiClient.createMeaningTag(name);
      const createdTag = {
        id: created.id ?? '',
        name: created.name ?? '',
        cardCount: 0,
      };
      if (!createdTag.id || !createdTag.name) return 'error';

      setAllTags((current) => [...current, createdTag]
        .sort((left, right) => left.name.localeCompare(right.name)));
      setTotalTagCount((count) => count + 1);
      setTagStatisticsAttempt((attempt) => attempt + 1);
      return 'created';
    } catch (exception) {
      return ApiException.isApiException(exception) && exception.status === 409 ? 'conflict' : 'error';
    }
  };

  // Rename has no backend endpoint yet, so this only updates local state (mocked) — see ManageTagsDialog.
  const handleTagRename = (tagId: string, name: string) => {
    setAllTags((current) => current.map((tag) => (tag.id === tagId ? { ...tag, name } : tag)));
  };

  const handleTagDelete = async (tagIds: string[]): Promise<boolean> => {
    try {
      await Promise.all(tagIds.map((tagId) => apiClient.deleteMeaningTag(tagId)));
      setAllTags((current) => current.filter((tag) => !tagIds.includes(tag.id)));
      setTotalTagCount((count) => Math.max(0, count - tagIds.length));
      if (selectedTagId && tagIds.includes(selectedTagId)) {
        setSelectedTagId(null);
      }
      setTagStatisticsAttempt((attempt) => attempt + 1);
      return true;
    } catch {
      setError('Unable to delete tag.');
      return false;
    }
  };

  const handlePin = async (exampleIndex: number, pinned: boolean) => {
    if (!selectedCard?.id || regeneratingCard || regeneratingExamples) return;

    try {
      setPinning(`${exampleIndex}`);
      setError('');
      await apiClient.pinMeaningExample(selectedCard.id, exampleIndex, pinned);
      setMeanings((current) => current.map((card) =>
        card.id !== selectedCard.id
          ? card
          : {
              ...card,
              examples: card.examples.map((example) =>
                example.exampleIndex === exampleIndex ? { ...example, pinned } : example
              ),
            }
      ));
    } catch {
      setError('Unable to update example pin.');
    } finally {
      setPinning('');
    }
  };

  const handleRegenerateExamples = async () => {
    if (!selectedCard?.id || regeneratingCard || regeneratingExamples || pinning) return;

    try {
      setRegeneratingExamples(true);
      setError('');
      const examples = await apiClient.regenerateMeaningExamples(selectedCard.id);
      const normalizedExamples = examples.map((example, exampleIndex) => ({
        exampleIndex,
        text: example.text ?? '',
        translation: example.translation ?? '',
        pinned: example.pinned ?? false,
      }));

      setMeanings((current) => current.map((card) =>
        card.id !== selectedCard.id
          ? card
          : { ...card, examples: normalizedExamples }
      ));
    } catch {
      setError('Unable to generate other examples.');
    } finally {
      setRegeneratingExamples(false);
    }
  };

  const navigationState = location.state as DetailNavigationState | null;
  const returnTo = navigationState?.returnTo ?? '/meaning';
  const backLabel = returnTo === '/training' ? 'Training' : 'Meaning';
  // Once the card has finished expanding, drop the offset and the matching scroll together: the
  // page paints identically, but from now on the detail view sits at the top of the document.
  const settleDetailScrollFrame = () => {
    const offset = detailScrollOffsetRef.current;
    if (!offset) {
      return;
    }

    window.clearTimeout(settleTimeoutRef.current);
    detailScrollOffsetRef.current = 0;
    setAppShellOffset(0);
    window.scrollTo({ left: 0, top: Math.max(0, window.scrollY - offset) });
    releaseDocumentScrollRoom();
  };

  const detailBack = () => {
    // Put the page back into the list's scroll frame before leaving, so the card shrinks back to
    // the row it came from instead of flying off the top of the screen.
    const savedScrollTop = Number(sessionStorage.getItem(meaningScrollPositionKey) ?? 0);
    // Only worth it while the detail view is still at its own top; once the user has scrolled
    // through the card there is no continuous path back to the row anyway.
    const detailAtTop = window.scrollY === detailScrollOffsetRef.current;
    if (!reduceMotion && returnTo === '/meaning' && savedScrollTop > 0 && detailAtTop) {
      window.clearTimeout(settleTimeoutRef.current);
      reserveDocumentScrollRoom(savedScrollTop);
      detailScrollOffsetRef.current = savedScrollTop;
      // Nothing moves on screen: the shell goes down by exactly what the scroll goes up. The offset
      // is dropped again in the layout effect below, once the list has taken over.
      setAppShellOffset(savedScrollTop);
      window.scrollTo({ left: 0, top: savedScrollTop });
      scrollHandledManually.current = true;
    }

    if (navigationState?.useHistoryBack) {
      navigate(-1);
      return;
    }

    navigate(returnTo);
  };

  // On desktop, the header and the This week/Tags column stay mounted behind the detail view too —
  // only the feed + Live mentions area is replaced by the card (design bundle screens 13a/13b:
  // opening a card doesn't navigate away or overlay a modal, it swaps the center/right content in
  // place). Picked reactively via isDesktopLayout, not the usual md:hidden/hidden:md:flex CSS pair,
  // so ExpandedMeaningCard — with its own live-mentions/videos fetches — mounts once, not twice.
  const manageTagsDialog = (
    <ManageTagsDialog
      open={manageTagsOpen}
      tags={allTags}
      onOpenChange={setManageTagsOpen}
      onRename={handleTagRename}
      onDelete={handleTagDelete}
    />
  );

  const wrapDesktopDetail = (content: ReactNode) =>
    isDesktopLayout ? (
      <MeaningDesktopDetailShell
        allTags={allTags}
        selectedTagId={selectedTagId}
        onSelectTag={(tagId) => {
          setSelectedTagId(tagId);
          navigate('/meaning');
        }}
        onCreateTag={handleTagCreate}
        onManageTags={() => setManageTagsOpen(true)}
        totalTagCount={totalTagCount}
        totalCardCount={totalCardCount}
        tagStatisticsLoading={tagStatisticsLoading}
        tagStatisticsError={tagStatisticsError}
        onRetryTagStatistics={() => setTagStatisticsAttempt((attempt) => attempt + 1)}
        weeklyStatistics={weeklyStatistics}
        weeklyStatisticsLoading={weeklyStatisticsLoading}
        weeklyStatisticsError={weeklyStatisticsError}
        onRetryWeeklyStatistics={() => setWeeklyStatisticsAttempt((attempt) => attempt + 1)}
      >
        {content}
      </MeaningDesktopDetailShell>
    ) : (
      content
    );

  if (meaningId && detailLoadState === 'loading' && !selectedCard) {
    return wrapDesktopDetail(<MeaningDetailStateCard onBack={detailBack} backLabel={backLabel} />);
  }

  if (meaningId && (detailLoadState === 'error' || !selectedCard)) {
    return wrapDesktopDetail(
      <MeaningDetailStateCard
        onBack={detailBack}
        backLabel={backLabel}
        error={detailLoadError || 'This card is unavailable. Please try again.'}
        onRetry={() => setDetailLoadAttempt((attempt) => attempt + 1)}
      />
    );
  }

  if (selectedCard) {
    const expandedCard = (
      <ExpandedMeaningCard
        card={selectedCard}
        allTags={allTags}
        error={error}
        updating={updating}
        regeneratingCard={regeneratingCard}
        deleting={deleting}
        pinning={pinning}
        regeneratingExamples={regeneratingExamples}
        savingTags={savingTags}
        onCardUpdate={handleCardUpdate}
        onCardRegenerate={handleCardRegenerate}
        onCardDelete={handleCardDelete}
        onPin={handlePin}
        onRegenerate={handleRegenerateExamples}
        alternativesFailed={Boolean(selectedCard.id && failedAlternatives[selectedCard.id])}
        onAlternativesRetry={() => selectedCard.id && void loadAlternatives(selectedCard.id)}
        onTagsUpdate={handleTagsUpdate}
        onBack={detailBack}
        onLayoutSettled={settleDetailScrollFrame}
        backLabel={backLabel}
      />
    );

    return isDesktopLayout ? (
      <>
        {wrapDesktopDetail(expandedCard)}
        {manageTagsDialog}
      </>
    ) : (
      <div className="flex flex-1 flex-col">{expandedCard}</div>
    );
  }

  return (
    <div className="min-h-screen">
      <main className="min-h-dvh overflow-x-hidden scroll-smooth bg-[#f6f4ef] text-slate-950 md:hidden">
        <div className="mx-auto flex min-h-dvh w-full max-w-3xl flex-col px-4 pb-[calc(9.5rem+env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] font-[family-name:var(--font-meaning-sans)] sm:px-6">
          <div className="mb-4">
            <ThisWeekWidget
              statistics={weeklyStatistics}
              loading={weeklyStatisticsLoading}
              error={weeklyStatisticsError}
              onRetry={() => setWeeklyStatisticsAttempt((attempt) => attempt + 1)}
            />
          </div>

          {/* No background fill: a blur alone hides text scrolling underneath while the
              large, smooth backdrop shapes read through almost untouched. The bottom mask
              keeps card edges from cutting a hard line across the blur boundary. */}
          <section aria-label="Search meanings" className="sticky top-0 z-10 -mx-4 bg-[#f6f4ef]/90 px-4 pb-4 pt-2 backdrop-blur-lg mask-b-from-90% sm:-mx-6 sm:px-6">
            <label htmlFor="meaning-search" className="sr-only">
              Search words
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-[18px] -translate-y-1/2 text-[#a39c8d]" />
              <Input
                id="meaning-search"
                type="search"
                inputMode="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search words or tags"
                className="h-13 rounded-full border-[#e6e2da] bg-white pl-12 pr-4 text-[16px] text-[#1e1c18] shadow-none focus-visible:border-[#2f5c4e] focus-visible:ring-0"
              />
            </div>
            {allTags.length > 0 && (
              <div className="-mx-1 mt-3 flex gap-2 overflow-x-auto overflow-y-hidden px-1 py-1">
                {allTags.map((tag) => {
                  const selected = selectedTagId === tag.id;
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setSelectedTagId(tag.id)}
                      className={cn(
                        'relative shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition before:absolute before:-inset-2 before:content-[\'\']',
                        selected
                          ? 'border border-[#2f5c4e] bg-[#2f5c4e] text-white shadow-sm'
                          : getTagColor(tag.name)
                      )}
                    >
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section aria-label="Word cards" className="flex flex-col gap-4">
            {error && (
              <div role="alert" className="flex flex-col gap-3 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
                <span>{error}</span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRetryCount((count) => count + 1)}
                  className="h-8 shrink-0 rounded-full border-red-200 bg-white text-red-700 hover:bg-red-100"
                >
                  Try again
                </Button>
              </div>
            )}

            {loading && (
              <div className="rounded-lg border border-white bg-white px-6 py-10 text-center text-sm text-slate-500 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                Loading meanings...
              </div>
            )}

            <AnimatePresence mode="popLayout">
              {!loading && meanings.map((card, index) => {
                const layoutId = getMeaningLayoutId(card, index);

                return (
                  <MeaningFeedCard
                    key={layoutId}
                    card={card}
                    layoutId={layoutId}
                    onOpen={() => openCard(card)}
                  />
                );
              })}
            </AnimatePresence>

            {!loading && meanings.length === 0 && (
              <div className="rounded-lg border border-dashed border-[#e6e2da] bg-white/70 px-6 py-10 text-center text-sm leading-6 text-[#7b7669]">
                No matching cards yet.
              </div>
            )}

            {!loading && hasMore && (
              <div ref={setMobileLoadMoreTrigger}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={loadingMore || !nextCursor}
                  className="mt-2 h-12 w-full rounded-full border-[#e6e2da] bg-white text-[#1e1c18] shadow-none hover:bg-[#f6f4ef]"
                >
                  {loadingMore ? (
                    <>
                      <LoaderCircle className="size-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </div>
            )}
          </section>

          <div className="mt-4">
            <LiveMentionsWidget />
          </div>
        </div>
      </main>

      {/* Desktop layout — matches design bundle screens 13a/13b ("Card Toolbar Options"). */}
      <div className="hidden min-h-dvh flex-col bg-[#f6f4ef] md:flex">
        <AppTopHeader />
        {/* Same column grid as AppTopHeader (see layoutGrid.ts) so the feed card, and both side
            panels, line up under the logo/nav/search/account above. Rendered feed card is exactly
            632px wide at the reference (~1364px) frame width — same as the design — and both side
            panels are the same width as each other and exactly half the card's (316px each). */}
        <div className={cn('mx-auto grid w-full flex-1 items-start', MEANING_GRID_COLS_CLASS, MEANING_ROW_MAX_WIDTH_CLASS)}>
          <motion.aside
            layoutId={MEANING_LEFT_RAIL_LAYOUT_ID}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="sticky top-[66px] flex min-w-0 max-h-[calc(100dvh-66px)] flex-col gap-3.5 self-start overflow-y-auto p-4"
          >
            <ThisWeekWidget
              statistics={weeklyStatistics}
              loading={weeklyStatisticsLoading}
              error={weeklyStatisticsError}
              onRetry={() => setWeeklyStatisticsAttempt((attempt) => attempt + 1)}
            />
            <TagsSidebarList
              tags={allTags}
              selectedTagId={selectedTagId}
              onSelect={setSelectedTagId}
              onCreate={handleTagCreate}
              onManage={() => setManageTagsOpen(true)}
              totalTagCount={totalTagCount}
              totalCardCount={totalCardCount}
              loading={tagStatisticsLoading}
              error={tagStatisticsError}
              onRetry={() => setTagStatisticsAttempt((attempt) => attempt + 1)}
            />
          </motion.aside>

          <main className="min-w-0 px-[18px] pb-24 pt-[18px]">
            {error && (
              <div role="alert" className="mb-3.5 flex flex-col gap-3 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
                <span>{error}</span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRetryCount((count) => count + 1)}
                  className="h-8 shrink-0 rounded-full border-red-200 bg-white text-red-700 hover:bg-red-100"
                >
                  Try again
                </Button>
              </div>
            )}

            {loading && (
              <div className="mb-3.5 rounded-lg border border-white bg-white px-6 py-10 text-center text-sm text-slate-500 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                Loading meanings...
              </div>
            )}

            <AnimatePresence mode="popLayout">
              {!loading && meanings.map((card, index) => {
                const layoutId = getMeaningLayoutId(card, index);

                return (
                  <MeaningFeedCard
                    key={layoutId}
                    card={card}
                    onOpen={() => openCard(card)}
                  />
                );
              })}
            </AnimatePresence>

            {!loading && meanings.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 px-6 py-10 text-center text-sm leading-6 text-slate-500">
                No matching cards yet.
              </div>
            )}

            {!loading && hasMore && (
              <div ref={setDesktopLoadMoreTrigger}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={loadingMore || !nextCursor}
                  className="mt-2 h-12 rounded-full border-white bg-white text-slate-800 shadow-[0_10px_30px_rgba(15,23,42,0.07)] hover:bg-slate-50"
                >
                  {loadingMore ? (
                    <>
                      <LoaderCircle className="size-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </div>
            )}
          </main>

          <aside className="sticky top-[66px] min-w-0 max-h-[calc(100dvh-66px)] self-start overflow-y-auto p-4">
            <LiveMentionsWidget />
          </aside>
        </div>
      </div>

      <CreateCardFab
        onContextual={() => setCreateDialog('contextual')}
        onManual={() => setCreateDialog('manual')}
      />

      <ContextualCardDialog
        open={createDialog === 'contextual'}
        initialText={search.trim()}
        creating={creating}
        error={createError}
        allTags={allTags}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeCreateDialog();
        }}
        onSubmit={handleContextualSubmit}
      />

      <ManualCardDialog
        open={createDialog === 'manual'}
        creating={creating}
        error={createError}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeCreateDialog();
        }}
        onSubmit={handleManualCreate}
      />

      {manageTagsDialog}
    </div>
  );
}
